'use client'

import type { Chapter, Course, Video } from '@/payload-types'

import { CourseLayout } from './CourseLayout'
import { CoursePurchaseButton } from './CoursePurchaseButton'
import { CourseReviews } from './CourseReviews'
import RichText from '@/components/RichText'
import { CourseVideo } from '@/components/Video/CourseVideo'
import { formatPriceFromEuros } from '@/utilities/formatPriceFromEuros'
import { BookOpenIcon, CheckIcon, CheckCircle2Icon, ChevronLeftIcon, ChevronRightIcon, CreditCard, LockIcon } from 'lucide-react'
import Link from 'next/link'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { useState } from 'react'

type Props = {
  course: Course
  activeChapterSlug: string
  hasPurchased?: boolean
  marketingChapter?: Chapter | null
}

const INTRO_SLUG = 'welkom'

export const CourseChapterPageContent = ({ course, activeChapterSlug, hasPurchased = false, marketingChapter }: Props) => {
  const formattedPrice = formatPriceFromEuros(course.priceEuro)
  const marketingChapterSlug = marketingChapter?.slug
  const allChapters = (course.chapters ?? []).filter(Boolean) as Chapter[]
  // Hide marketing chapter from purchased users — it's only a preview for non-purchasers
  const chapters = hasPurchased
    ? allChapters.filter((c) => c.slug !== marketingChapterSlug)
    : allChapters
  const courseSlug = course.slug

  const isMarketingSlug = activeChapterSlug === marketingChapter?.slug
  const isIntroPage = activeChapterSlug === INTRO_SLUG && !(!hasPurchased && isMarketingSlug)

  // For intro page, activeIndex is -1 (before all chapters)
  // For chapters, find the chapter index
  const activeIndex = isIntroPage
    ? -1
    : Math.max(0, chapters.findIndex((c) => c?.slug === activeChapterSlug))
  const activeChapter = isIntroPage ? null : chapters[activeIndex]

  // Navigation: intro (-1) -> chapter 0 -> chapter 1 -> ...
  const prevChapter = isIntroPage ? null : activeIndex === 0 ? 'intro' : chapters[activeIndex - 1]
  const nextChapter = isIntroPage
    ? chapters[0]
    : activeIndex < chapters.length - 1
      ? chapters[activeIndex + 1]
      : null

  // Determine if the current chapter is the marketing chapter
  const isViewingMarketingChapter = !hasPurchased && isMarketingSlug
  // User can view content if they purchased, or if viewing the marketing chapter
  const canViewContent = hasPurchased || isViewingMarketingChapter

  const [purchaseSheetOpen, setPurchaseSheetOpen] = useState(false)

  const chapterHref = (chapter?: Chapter | 'intro' | null) => {
    if (chapter === 'intro') return `/cursussen/${courseSlug}/${INTRO_SLUG}`
    return chapter ? `/cursussen/${courseSlug}/${chapter.slug}` : undefined
  }

  return (
    <>
      <div className="min-h-screen overflow-x-hidden animate-in fade-in duration-300">
        {/* Hero Header */}
        <div className="relative bg-gradient-to-b from-[#085e41]/12 via-[#085e41]/5 to-background">
          {/* Decorative circles */}
          <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[#085e41]/8 pointer-events-none" />
          <div className="absolute -left-8 top-1/3 h-48 w-48 rounded-full bg-[#085e41]/10 pointer-events-none" />
          <div className="absolute -right-6 top-1/2 h-32 w-32 rounded-full border-2 border-[#085e41]/15 pointer-events-none" />
          <div className="absolute right-1/4 top-6 h-16 w-16 rounded-full border-2 border-[#085e41]/10 pointer-events-none" />
          <div className="mx-auto w-full max-w-6xl px-4 pb-4 pt-6 sm:pb-10 sm:pt-10 sm:px-6 lg:px-8">
            {/* Breadcrumb — hidden on marketing/landing pages */}
            {!isViewingMarketingChapter && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Link
                  className="inline-flex items-center gap-1.5 font-medium transition-colors hover:text-foreground"
                  href="/cursussen"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  Cursussen
                </Link>
                <span>/</span>
                <Link
                  className="font-medium transition-colors hover:text-foreground"
                  href={`/cursussen/${courseSlug}`}
                >
                  {course.title}
                </Link>
              </div>
            )}

            <header className="mt-8 space-y-5 hidden lg:block">
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full border border-[#085e41]/20 bg-white/80 px-4 py-2 text-sm font-medium text-[#085e41] shadow-sm backdrop-blur-sm">
                  <BookOpenIcon className="h-4 w-4" />
                  {isIntroPage ? 'Welkom' : isViewingMarketingChapter ? 'Voorbeeld' : `Hoofdstuk ${activeIndex + 1} van ${chapters.length}`}
                </span>
              </div>

              <h1 className="text-balance text-3xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                {isIntroPage ? course.title : isViewingMarketingChapter ? (marketingChapter?.title || course.title) : (activeChapter?.title || course.title)}
              </h1>

              {/* Hero CTA — mobile/tablet only, non-purchased users */}
              {!hasPurchased && (
                <button
                  onClick={() => setPurchaseSheetOpen(true)}
                  className="lg:hidden inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-base font-semibold text-primary-foreground shadow-md shadow-primary/20 transition-all hover:bg-primary/90"
                >
                  <CreditCard className="h-4 w-4" />
                   Koop nu
                </button>
              )}

              {/* Progress indicator (only for purchased users) */}
              {hasPurchased && (
                <div className="flex items-center gap-4 pt-2">
                  <div className="flex-1 h-2 bg-[#085e41]/15 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#085e41] rounded-full transition-all duration-300"
                      style={{ width: `${((activeIndex + 2) / (chapters.length + 1)) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">
                    {Math.round(((activeIndex + 2) / (chapters.length + 1)) * 100)}%
                  </span>
                </div>
              )}
            </header>
          </div>
        </div>

        <div className="mx-auto w-full max-w-6xl px-4 pb-12 sm:px-6 lg:pb-16 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          {/* Sidebar navigation */}
          <aside className={`lg:col-span-4${!hasPurchased ? ' hidden lg:block' : ''}`}>
            <div className="sticky top-24 space-y-4">
            <nav
              aria-label="Hoofdstukken"
              className={`space-y-6 rounded-2xl border border-border/60 bg-card p-6 shadow-sm${!hasPurchased ? ' hidden lg:block' : ''}`}
            >
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Inhoud
                </p>
                {hasPurchased && (
                <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                  {activeIndex + 2}/{chapters.length + 1}
                </span>
                )}
              </div>

              <ol className="space-y-1">
                {/* Marketing chapter item (for non-purchased users, when not in chapters list) */}
                {!hasPurchased && marketingChapter && !chapters.some((c) => c.slug === marketingChapterSlug) && (
                  <li>
                    <Link
                      className={`group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all ${
                        isViewingMarketingChapter
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'hover:bg-muted/50'
                      }`}
                      href={`/cursussen/${courseSlug}/${marketingChapterSlug}`}
                    >
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium transition-colors ${
                          isViewingMarketingChapter
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary'
                        }`}
                      >
                        ★
                      </span>
                      <span
                        className={
                          isViewingMarketingChapter
                            ? 'text-primary'
                            : 'text-foreground/80 group-hover:text-foreground'
                        }
                      >
                        {marketingChapter.title}
                      </span>
                    </Link>
                  </li>
                )}
                {/* Introduction item (only for purchased users) */}
                {hasPurchased && (
                <li>
                  <Link
                    className={`group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all ${
                      isIntroPage
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'hover:bg-muted/50'
                    }`}
                    href={`/cursussen/${courseSlug}/${INTRO_SLUG}`}
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium transition-colors ${
                        isIntroPage
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-primary/20 text-primary'
                      }`}
                    >
                      {!isIntroPage ? (
                        <CheckIcon className="h-4 w-4 stroke-[3]" />
                      ) : (
                        '★'
                      )}
                    </span>
                    <span
                      className={
                        isIntroPage
                          ? 'text-primary'
                          : 'text-foreground/80 group-hover:text-foreground'
                      }
                    >
                      Welkom
                    </span>
                  </Link>
                </li>
                )}
                {/* Chapter items (only show all chapters to purchased users) */}
                {hasPurchased && chapters.map((chapter, i) => {
                  const href = chapterHref(chapter)
                  const isActive = !isIntroPage && chapter.slug === activeChapter?.slug
                  const isCompleted = !isIntroPage && i < activeIndex
                  return (
                    <li key={chapter.id ?? chapter.slug}>
                      <Link
                        className={`group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all ${
                          isActive
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'hover:bg-muted/50'
                        }`}
                        href={href!}
                      >
                        <span
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium transition-colors ${
                            isActive
                              ? 'bg-primary text-primary-foreground'
                              : isCompleted
                                ? 'bg-primary/20 text-primary'
                                : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary'
                          }`}
                        >
                          {isCompleted ? (
                            <CheckIcon className="h-4 w-4 stroke-[3]" />
                          ) : (
                            i + 1
                          )}
                        </span>
                        <span
                          className={
                            isActive
                              ? 'text-primary'
                              : 'text-foreground/80 group-hover:text-foreground'
                          }
                        >
                          {chapter.title}
                        </span>
                      </Link>
                    </li>
                  )
                })}
              </ol>

              {/* Sidebar navigation buttons (only for purchased users) */}
              {hasPurchased && (
              <div className="flex items-center gap-3 pt-4 border-t border-border/50">
                <Link
                  aria-disabled={!prevChapter}
                  className={`flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-border/60 px-4 py-2.5 text-sm font-medium transition-all ${
                    prevChapter
                      ? 'text-foreground hover:bg-muted/50 hover:text-primary hover:border-primary/30'
                      : 'pointer-events-none opacity-40'
                  }`}
                  href={chapterHref(prevChapter) || '#'}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  Vorige
                </Link>

                <Link
                  aria-disabled={!nextChapter}
                  className={`flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-border/60 px-4 py-2.5 text-sm font-medium transition-all ${
                    nextChapter
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90 border-primary'
                      : 'pointer-events-none opacity-40'
                  }`}
                  href={chapterHref(nextChapter) || '#'}
                >
                  Volgende
                  <ChevronRightIcon className="h-4 w-4" />
                </Link>
              </div>
              )}

            </nav>

              {/* Purchase button for non-purchased users */}
              {!hasPurchased && (
                <CoursePurchaseButton course={course} />
              )}
            </div>
          </aside>

          {/* Main content: single chapter or introduction */}
          <div className="lg:col-span-8 space-y-10 min-w-0">
            {/* Content wrapper */}
            <div className="relative z-10 rounded-2xl border border-primary/30 bg-card p-6 lg:p-8 overflow-hidden shadow-md">
              {isIntroPage ? (
                <section className="space-y-4">
                  <div className="space-y-1 pb-2">
                    <p className="text-xs font-semibold uppercase tracking-widest text-primary">
                      Welkom
                    </p>
                    <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-[#085e41] to-[#2D5A3D] bg-clip-text text-transparent">{course.title}</h2>
                  </div>

                  {/* Intro Video */}
                  {course.introVideo && typeof course.introVideo === 'object' && (
                    <div className="overflow-hidden rounded-xl border border-border/40">
                      <CourseVideo
                        video={{
                          id: String((course.introVideo as Video).id),
                          title: (course.introVideo as Video).title,
                          muxPlaybackId: (course.introVideo as Video).muxPlaybackId || undefined,
                        }}
                        className="w-full"
                      />
                    </div>
                  )}

                  <div className="space-y-10">
                    {course.overview ? (
                      <RichText
                        data={course.overview}
                        className="prose prose-lg prose-neutral max-w-none dark:prose-invert prose-headings:font-semibold prose-p:leading-relaxed prose-a:text-primary prose-a:no-underline hover:prose-a:underline"
                        enableGutter={false}
                      />
                    ) : (
                      <p className="text-lg leading-relaxed text-muted-foreground">
                        Welkom bij deze cursus. Klik op &quot;Volgende&quot; om te beginnen met het eerste hoofdstuk.
                      </p>
                    )}
                  </div>
                </section>
              ) : canViewContent && isViewingMarketingChapter && marketingChapter ? (
                <>
                  <CourseLayout activeChapterIndex={0} chapters={[marketingChapter] as any} hideChapterHeader />
                  {!hasPurchased && (
                    <>
                      <CourseReviews className="mt-8 pt-8 border-t border-border/50" />
                      <div className="mt-8 pt-8 border-t border-border/50">
                        <CoursePurchaseButton course={course} />
                      </div>
                    </>
                  )}
                </>
              ) : canViewContent ? (
                <CourseLayout activeChapterIndex={activeIndex} chapters={chapters as any} />
              ) : (
                <section className="space-y-8 text-center py-12">
                  <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-muted">
                    <LockIcon className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-2xl font-bold tracking-tight text-foreground">
                      Dit hoofdstuk is vergrendeld
                    </h2>
                    <p className="text-muted-foreground max-w-md mx-auto">
                      Koop deze cursus om toegang te krijgen tot alle hoofdstukken.
                    </p>
                  </div>
                  <CoursePurchaseButton course={course} className="max-w-sm mx-auto" />
                </section>
              )}
            </div>

            {/* Bottom navigation (hide on marketing page) */}
            {canViewContent && !isViewingMarketingChapter && <div className="flex flex-col sm:flex-row items-stretch gap-3 sm:gap-4">
              {prevChapter ? (
                <Link
                  className="group flex-1 flex flex-col items-start gap-1.5 sm:gap-2 rounded-xl border border-border/60 bg-card/30 p-4 sm:p-6 transition-all hover:border-primary/30 hover:bg-card/60 hover:shadow-md"
                  href={chapterHref(prevChapter)!}
                >
                  <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-muted-foreground group-hover:text-primary">
                    <ChevronLeftIcon className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
                    {prevChapter === 'intro' ? 'Terug naar welkom' : 'Vorige hoofdstuk'}
                  </span>
                  <span className="text-base sm:text-lg font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                    {prevChapter === 'intro' ? 'Welkom' : prevChapter.title}
                  </span>
                </Link>
              ) : (
                <div className="hidden sm:block flex-1" />
              )}

              {nextChapter ? (
                <Link
                  className="group flex-1 flex flex-col items-end gap-1.5 sm:gap-2 rounded-xl border border-primary/30 bg-primary/5 p-4 sm:p-6 transition-all hover:bg-primary/10 hover:shadow-md"
                  href={chapterHref(nextChapter)!}
                >
                  <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-primary">
                    {isIntroPage ? 'Start met hoofdstuk 1' : 'Volgende hoofdstuk'}
                    <ChevronRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                  <span className="text-base sm:text-lg font-semibold text-foreground group-hover:text-primary transition-colors text-right line-clamp-2">
                    {nextChapter.title}
                  </span>
                </Link>
              ) : (
                <Link
                  className="group flex-1 flex flex-col items-end gap-1.5 sm:gap-2 rounded-xl border border-green-500/30 bg-green-500/5 p-4 sm:p-6 transition-all hover:bg-green-500/10 hover:shadow-md"
                  href={`/cursussen/${courseSlug}`}
                >
                  <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-green-600 dark:text-green-400">
                    <CheckCircle2Icon className="h-4 w-4" />
                    Cursus voltooid
                  </span>
                  <span className="text-base sm:text-lg font-semibold text-foreground text-right">
                    Terug naar overzicht
                  </span>
                </Link>
              )}
            </div>}
          </div>
        </div>
        </div>
      </div>

      {/* Sticky bottom CTA + Sheet — mobile/tablet only, non-purchased users */}
      {!hasPurchased && (
        <>
          <div className="fixed bottom-0 left-0 right-0 z-50 border-t-2 border-primary/20 bg-background shadow-[0_-4px_24px_rgba(0,0,0,0.12)] px-6 py-4 lg:hidden will-change-transform [transform:translateZ(0)]">
            <div className="mx-auto flex max-w-lg items-center gap-4">
              <div className="flex-1">
                {course.priceFrom != null && (
                  <p className="text-xs text-muted-foreground line-through">{formatPriceFromEuros(course.priceFrom)}</p>
                )}
                <p className="text-xl font-bold text-primary">{formattedPrice}</p>
              </div>
              <button
                onClick={() => setPurchaseSheetOpen(true)}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-base font-bold text-primary-foreground shadow-lg shadow-primary/30 transition-all hover:bg-primary/90 active:scale-[0.98]"
              >
                <CreditCard className="h-4 w-4" />
                Koop nu
              </button>
            </div>
          </div>
          <Sheet open={purchaseSheetOpen} onOpenChange={setPurchaseSheetOpen}>
            <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto px-4 pb-8 pt-6">
              <SheetTitle className="sr-only">Koop deze cursus</SheetTitle>
              <CoursePurchaseButton course={course} bare />
            </SheetContent>
          </Sheet>
        </>
      )}
    </>
  )
}
